import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { ClickAwayListener, Popper, Fade, Typography } from '@mui/material';
import { useHandleCapturedEventsRef } from '../../hooks/handleCapturedEventsRef/useHandleCapturedEventsRef.js';
import { theme } from '../../utils/themeProvider.js';
import { customArcherColors } from '../../utils/customArcherColors.js';
import '@mui/styles';
import '@mui/material/styles';
import 'uuid/v4';
import { hexToRGB } from '../../utils/hexToRGB.js';
import { IconButton } from '../IconButton/IconButton.js';
import { LayoutGrid } from '../LayoutGrid/LayoutGrid.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
var keyListeners = {
  escape: 'Escape'
};

var isAlert = function isAlert(icon) {
  return !!(icon && (icon === 'Info' || icon === 'Warning' || icon === 'Success' || icon === 'Error'));
};

var setActiveAndFocus = function setActiveAndFocus(iconTrueColor) {
  return hexToRGB(iconTrueColor, 0.15);
};

var useStyles = function useStyles(props) {
  return {
    popperContainer: {
      zIndex: 'modal',
      width: '425px',
      borderRadius: '3px',
      boxShadow: '0 2px 10px 0 rgba(33,33,33,.40)',
      padding: '1rem',
      backgroundColor: 'background.paper'
    },
    infoPopperButton: {
      padding: '0.5rem',
      backgroundColor: 'transparent',
      color: props.iconTrueColor,
      '&:hover': {
        borderRadius: '50%',
        backgroundColor: props.hoverSetting
      },
      '&:focus': {
        backgroundColor: props.activeAndFocusSetting
      }
    },
    infoPopperButtonActive: {
      padding: '0.5rem',
      color: props.iconTrueColor,
      backgroundColor: props.activeAndFocusSetting,
      '&:hover': {
        borderRadius: '50%',
        backgroundColor: props.hoverSetting
      },
      '&:focus': {
        backgroundColor: props.activeAndFocusSetting
      }
    }
  };
};

var InfoPopperBase = function InfoPopperBase(_ref) {
  var buttonDataProp = _ref.buttonDataProp,
      icon = _ref.icon,
      iconColor = _ref.iconColor,
      fontSize = _ref.fontSize,
      tooltip = _ref.tooltip,
      disabled = _ref.disabled,
      popperPosition = _ref.popperPosition,
      className = _ref.className,
      contentItem = _ref.contentItem,
      _ref$childContentRefs = _ref.childContentRefs,
      childContentRefs = _ref$childContentRefs === void 0 ? [] : _ref$childContentRefs,
      _ref$overrideClickAwa = _ref.overrideClickAwayRefs,
      overrideClickAwayRefs = _ref$overrideClickAwa === void 0 ? [] : _ref$overrideClickAwa,
      sxButton = _ref.sxButton,
      sxPopper = _ref.sxPopper;
  var isAlertIcon = isAlert(icon);
  var iconTrueColor = isAlertIcon ? customArcherColors.getCustomAlertColors(iconColor) : iconColor || theme.palette.primary.main;
  var activeAndFocusSetting = setActiveAndFocus(iconTrueColor);
  var hoverSetting = hexToRGB(iconTrueColor, 0.07);
  var cssProps = {
    activeAndFocusSetting: activeAndFocusSetting,
    iconTrueColor: iconTrueColor,
    hoverSetting: hoverSetting
  };

  var _useStyles = useStyles(cssProps),
      popperContainer = _useStyles.popperContainer,
      infoPopperButton = _useStyles.infoPopperButton,
      infoPopperButtonActive = _useStyles.infoPopperButtonActive;

  var _React$useState = React.useState(null),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      anchorEl = _React$useState2[0],
      setAnchorEl = _React$useState2[1];

  var _React$useState3 = React.useState(false),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      open = _React$useState4[0],
      setOpen = _React$useState4[1];

  var _React$useState5 = React.useState(false),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      tooltipOpen = _React$useState6[0],
      setTooltipOpen = _React$useState6[1];

  var _React$useState7 = React.useState(),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      placement = _React$useState8[0],
      setPlacement = _React$useState8[1];

  var iconButtonRef = React.useRef(null);

  var handleTooltipClose = function handleTooltipClose() {
    setTooltipOpen(false);
  };

  var handleTooltipOpen = function handleTooltipOpen() {
    setTooltipOpen(true);
  };

  var handleClick = function handleClick(newPlacement) {
    return function (event) {
      event.preventDefault();
      setAnchorEl(event.currentTarget);
      setOpen(function (prev) {
        return placement !== newPlacement || !prev;
      });
      setPlacement(newPlacement);
      setTooltipOpen(false);
    };
  };

  var handleClickAway = React.useCallback(function (event) {
    var shouldOverrideHandleClickAway = overrideClickAwayRefs.some(function (overrideRef) {
      return overrideRef.current;
    });

    if (!shouldOverrideHandleClickAway) {
      var shouldKeepPopperOpen = childContentRefs.some(function (contentRef) {
        return contentRef.current && contentRef.current.contains(event.target);
      });
      setOpen(shouldKeepPopperOpen);
    }
  }, [setOpen, childContentRefs]);

  var handleKeyDown = function handleKeyDown(event) {
    if (event.key === keyListeners.escape) {
      setOpen(false);

      if (iconButtonRef.current) {
        iconButtonRef.current.blur();
      }
    }
  };

  var keyDownWrapperRef = useHandleCapturedEventsRef(null, {
    keydown: [handleKeyDown]
  });
  return /*#__PURE__*/React.createElement(ClickAwayListener, {
    onClickAway: handleClickAway
  }, /*#__PURE__*/React.createElement("div", {
    ref: keyDownWrapperRef
  }, /*#__PURE__*/React.createElement(LayoutGrid, {
    container: true,
    direction: "row",
    justify: "flex-start"
  }, /*#__PURE__*/React.createElement(Tooltip, {
    title: !open ? tooltip || '' : '',
    open: tooltipOpen,
    onClose: handleTooltipClose,
    onOpen: handleTooltipOpen
  }, /*#__PURE__*/React.createElement(IconButton, {
    sx: [open ? infoPopperButtonActive : infoPopperButton, sxButton],
    icon: icon === 'Info' ? 'InfoOutlined' : icon,
    onClick: handleClick(popperPosition || 'bottom'),
    ref: iconButtonRef,
    fontSize: fontSize || 'large',
    dataProp: buttonDataProp || 'icon',
    className: "infoPopperFilter",
    disabled: disabled || false,
    "aria-label": tooltip
  }))), /*#__PURE__*/React.createElement(Popper, {
    sx: [popperContainer, sxPopper],
    open: open,
    anchorEl: anchorEl,
    placement: placement,
    className: className,
    "aria-describedby": "infopopper",
    transition: true,
    disablePortal: true
  }, function (_ref2) {
    var TransitionProps = _ref2.TransitionProps;
    return /*#__PURE__*/React.createElement(Fade, _extends({}, TransitionProps, {
      timeout: 350
    }), /*#__PURE__*/React.createElement("div", null, typeof contentItem === 'string' ? /*#__PURE__*/React.createElement(Typography, {
      variant: "body1",
      color: "textPrimary",
      id: "infopopper"
    }, contentItem || '') : contentItem));
  })));
};

var InfoPopper = /*#__PURE__*/React.memo(InfoPopperBase);
InfoPopper.displayName = 'InfoPopper';
export { InfoPopper };